import { ReactNode, useRef, useState } from "react";

import {
  FetchNavigationArticlesDocument,
  FetchNavigationCollectionsDocument,
  FetchNavigationPagesDocument,
  FetchNavigationProductsDocument,
  useFetchNavigationQuery,
} from "@src/generated";
import { useDebouncedSearch } from "@src/hooks/useDebouncedSearch";
import { shopifyGqlClient } from "@src/providers/gqlProvider";
import { useSelector } from "@src/store";
import { ExperienceBuilderSelector } from "@src/store/slices/experienceBuilder";

import { GroupState, NavigationGroup } from "../types";

import {
  buildNavigationOptions,
  buildPath,
  PAGE_SIZE,
} from "./buildNavigationOptions";
import { NavigatorContext } from "./NavigatorContext";
import { parseSearchInput } from "./parseSearchInput";
import { isUrlInput } from "./validateNavigableUrl";

const LOAD_MORE_SIZE = 25;

const LOAD_MORE_DOCUMENTS = {
  pages: FetchNavigationPagesDocument,
  collections: FetchNavigationCollectionsDocument,
  products: FetchNavigationProductsDocument,
  articles: FetchNavigationArticlesDocument,
} as const;

/**
 * Navigator data source for the logged-in org's own store: the authenticated
 * Admin GraphQL API, with cursor-based "load more" per group.
 */
export const AuthenticatedNavigatorProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const path = useSelector(ExperienceBuilderSelector.getLastPath);
  const [inputValue, setInputValue] = useState("");
  const listboxRef = useRef<HTMLUListElement>(null);

  const debouncedSearch = useDebouncedSearch(inputValue);
  // A URL isn't a search. Suppress when either the live input or the lagging
  // debounced value looks like a URL, so navigating on Enter — which clears the
  // input while the debounce still holds the URL — doesn't fire a stale search.
  const searchQuery =
    isUrlInput(inputValue) || isUrlInput(debouncedSearch)
      ? ""
      : debouncedSearch;
  const parsed = searchQuery ? parseSearchInput(searchQuery) : undefined;
  const shopifyQuery = parsed
    ? parsed.group
      ? `handle:*${parsed.searchTerm}*`
      : `title:*${parsed.searchTerm}*`
    : undefined;

  const { data, loading: isLoading } = useFetchNavigationQuery({
    variables: { first: PAGE_SIZE + 1, query: shopifyQuery },
    client: shopifyGqlClient,
    fetchPolicy: "no-cache",
  });

  const [extraItems, setExtraItems] = useState<
    Partial<Record<NavigationGroup, GroupState>>
  >({});
  const [loadingGroup, setLoadingGroup] = useState<NavigationGroup>();

  // Reset extra items when search changes
  const [lastQuery, setLastQuery] = useState<string | undefined>(undefined);
  if (searchQuery !== lastQuery) {
    setLastQuery(searchQuery);
    setExtraItems({});
    setLoadingGroup(undefined);
  }

  const { options, groupHasMore, groupEndCursor } = buildNavigationOptions(
    data,
    extraItems,
    searchQuery,
    parsed?.group
  );

  const selectedOption = options.find((opt) => opt.path === path) ?? null;

  const showLoadMore = (groupKey: NavigationGroup) => !!groupHasMore[groupKey];

  const activeQueryRef = useRef(searchQuery);
  activeQueryRef.current = searchQuery;

  const onLoadMore = (group: NavigationGroup) => {
    const cursor = groupEndCursor[group];
    if (!cursor || loadingGroup) return;

    const queryAtStart = searchQuery;
    const savedScrollTop = listboxRef.current?.scrollTop ?? 0;
    const restoreScroll = () => {
      requestAnimationFrame(() => {
        if (listboxRef.current) {
          listboxRef.current.scrollTop = savedScrollTop;
        }
      });
    };

    setLoadingGroup(group);
    restoreScroll();

    void shopifyGqlClient
      .query({
        query: LOAD_MORE_DOCUMENTS[group],
        variables: {
          first: LOAD_MORE_SIZE + 1,
          after: cursor,
          query: shopifyQuery,
        },
        fetchPolicy: "no-cache",
      })
      .then((result) => {
        if (activeQueryRef.current !== queryAtStart) return;

        const connection = result.data?.[group];
        if (!connection) return;

        const edges = connection.edges;
        const hasMore = edges.length > LOAD_MORE_SIZE;
        const visibleEdges = hasMore ? edges.slice(0, -1) : edges;
        const endCursor =
          visibleEdges.length > 0
            ? visibleEdges[visibleEdges.length - 1].cursor
            : null;

        const newItems = visibleEdges.map((edge) => ({
          title: edge.node.title,
          path: buildPath(
            group,
            edge.node.handle,
            "blog" in edge.node ? edge.node.blog.handle : undefined
          ),
          group,
        }));

        setExtraItems((prev) => {
          const existing = prev[group];
          return {
            ...prev,
            [group]: {
              items: [...(existing?.items ?? []), ...newItems],
              endCursor,
              hasMore,
            },
          };
        });
        restoreScroll();
      })
      .catch((error: unknown) => {
        console.error(`[Navigator] Failed to load more ${group}:`, error);
      })
      .finally(() => {
        setLoadingGroup(undefined);
        restoreScroll();
      });
  };

  return (
    <NavigatorContext.Provider
      value={{
        inputValue,
        setInputValue,
        options,
        selectedOption,
        isLoading,
        loadingGroup,
        showLoadMore,
        onLoadMore,
        listboxRef,
      }}
    >
      {children}
    </NavigatorContext.Provider>
  );
};
